import { MutableRefObject, useCallback, useEffect } from 'react'
import { GridApiPro } from '@microservices/wiskey-react-table-component/dist/DataGrid/DataGridV7/models/gridApiPro'

import { setSelectionCounts } from '@redux/reducers/timeLine.reducer'

import { useAppDispatch } from '@hooks'
import {
  HandleTimelineChangeSelectedCells,
  HandleTimelineToggleSelectedEvent,
  ObjectDataRecord,
  SelectedCell,
  TimelineCellContext,
  TimeLineGridDataRecord,
} from '@types'

type Props = {
  cellContextRef: MutableRefObject<TimelineCellContext>
  apiRef?: MutableRefObject<GridApiPro> | null
  timeLineGridDataRef: MutableRefObject<TimeLineGridDataRecord[]>
}

function computeSelectionAggregates(map: Record<string, SelectedCell>) {
  const cellIds = Object.keys(map)
  const eventsCount = cellIds.reduce((sum, cellId) => sum + (map[cellId]?.events?.length || 0), 0)

  return { selectedCellsCount: cellIds.length, selectedEventsCount: eventsCount }
}

export const useCellSelection = (props: Props) => {
  const { cellContextRef, apiRef, timeLineGridDataRef } = props
  const dispatch = useAppDispatch()

  const refreshRowsByIds = useCallback(
    (rowIds: string[]) => {
      const uniq = [...new Set(rowIds)].filter(Boolean)
      const api = apiRef?.current

      if (!uniq.length || !api || typeof api.updateRows !== 'function') {
        return
      }

      const data = timeLineGridDataRef.current
      const rows = uniq
        .map(id => data.find(r => r.id === id))
        .filter((r): r is TimeLineGridDataRecord => Boolean(r))

      if (rows.length) {
        api.updateRows(rows)
      }
    },
    [apiRef, timeLineGridDataRef]
  )

  const updateSelectionCounts = useCallback(
    (map: Record<string, SelectedCell>) => {
      dispatch(setSelectionCounts(computeSelectionAggregates(map)))
    },
    [dispatch]
  )

  useEffect(() => {
    return () => {
      dispatch(setSelectionCounts({ selectedCellsCount: 0, selectedEventsCount: 0 }))
    }
  }, [])

  const handleChangeSelectedCells: HandleTimelineChangeSelectedCells = data => {
    const { cellId, objectXItems, columnId, rowData, rowId, startDate, endDate } = data
    const prev = cellContextRef.current.selectedCells

    if (prev[cellId]) {
      const newSelectedCells = { ...prev }

      delete newSelectedCells[cellId]

      cellContextRef.current = {
        ...cellContextRef.current,
        selectedCells: newSelectedCells,
      }
      updateSelectionCounts(newSelectedCells)
      refreshRowsByIds([rowId])

      return
    }

    const newSelectedCells = {
      ...prev,
      [cellId]: {
        columnId,
        rowData,
        rowId,
        startDate,
        endDate,
        events: [...objectXItems],
      },
    }

    cellContextRef.current = {
      ...cellContextRef.current,
      selectedCells: newSelectedCells,
    }
    updateSelectionCounts(newSelectedCells)
    refreshRowsByIds([rowId])
  }

  const handleClearSelectedCells = () => {
    const prev = cellContextRef.current.selectedCells
    const rowIds = [...new Set(Object.values(prev).map(c => c.rowId))]

    cellContextRef.current = {
      ...cellContextRef.current,
      selectedCells: {},
    }
    dispatch(setSelectionCounts({ selectedCellsCount: 0, selectedEventsCount: 0 }))
    refreshRowsByIds(rowIds)
  }

  const handleToggleSelectedEvent: HandleTimelineToggleSelectedEvent = data => {
    const { cellId, event, pkFieldName, endDate, startDate, rowData, rowId, columnId } = data
    const prevSelectedCells = cellContextRef.current.selectedCells

    const selectedCell = prevSelectedCells[cellId] ?? {
      endDate,
      startDate,
      rowData,
      rowId,
      columnId,
      events: [],
    }

    if (!pkFieldName) {
      return
    }

    const eventIndex = selectedCell?.events.findIndex(e => e[pkFieldName] === event[pkFieldName])

    let newEvents: ObjectDataRecord[]

    if (eventIndex >= 0) {
      newEvents = selectedCell?.events.filter((_, i) => i !== eventIndex)
    } else {
      newEvents = [...selectedCell.events, event]
    }

    if (newEvents.length === 0) {
      const rest = { ...prevSelectedCells }

      delete rest[cellId]

      cellContextRef.current = {
        ...cellContextRef.current,
        selectedCells: rest,
      }
      updateSelectionCounts(rest)
      refreshRowsByIds([rowId])

      return
    }

    const newSelectedCells = {
      ...prevSelectedCells,
      [cellId]: {
        ...selectedCell,
        events: [...newEvents],
      },
    }

    cellContextRef.current = {
      ...cellContextRef.current,
      selectedCells: newSelectedCells,
    }
    updateSelectionCounts(newSelectedCells)
    refreshRowsByIds([rowId])
  }

  return {
    handleClearSelectedCells,
    handleChangeSelectedCells,
    handleToggleSelectedEvent,
  }
}
